import { FormEvent, useMemo, useState } from 'react';
import { adminChallenges, fundingStatuses, offer, organizationTypes } from '../content/landing';

type LeadState = 'idle' | 'submitting' | 'success' | 'error';

function getUtmParams() {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source') || '',
    utm_medium: params.get('utm_medium') || '',
    utm_campaign: params.get('utm_campaign') || '',
    utm_content: params.get('utm_content') || '',
    utm_term: params.get('utm_term') || '',
  };
}

export function LeadForm() {
  const [state, setState] = useState<LeadState>('idle');
  const [message, setMessage] = useState('');
  const utms = useMemo(() => (typeof window !== 'undefined' ? getUtmParams() : {}), []);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setState('submitting');
    setMessage('');

    const form = event.currentTarget;
    const formData = new FormData(form);
    const payload = Object.fromEntries(formData.entries());

    try {
      const response = await fetch('/api/lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error('Lead submit failed');
      setState('success');
      setMessage('The one-pager is ready. Use the download link below, and MFC can follow up if a direct conversation makes sense.');
      form.reset();
      window.dispatchEvent(new CustomEvent('mfc:event', { detail: { name: 'form_submit' } }));
    } catch (error) {
      setState('error');
      setMessage('The form had a problem. Use the direct booking link or email moldedfortitude@gmail.com.');
    }
  }

  return (
    <form className="lead-form" onSubmit={handleSubmit}>
      <div className="form-grid">
        <label>
          First name
          <input name="first_name" autoComplete="given-name" required />
        </label>
        <label>
          Last name
          <input name="last_name" autoComplete="family-name" required />
        </label>
        <label>
          Email
          <input type="email" name="email" autoComplete="email" required />
        </label>
        <label>
          Phone optional
          <input name="phone" autoComplete="tel" />
        </label>
      </div>

      <label>
        Organization
        <input name="organization" required />
      </label>

      <label>
        Role
        <input name="role" required />
      </label>

      <label>
        Organization type
        <select name="organization_type" required defaultValue="">
          <option value="" disabled>Select one</option>
          {organizationTypes.map((item) => <option key={item}>{item}</option>)}
        </select>
      </label>

      <label>
        Biggest admin challenge
        <select name="biggest_admin_challenge" required defaultValue="">
          <option value="" disabled>Select one</option>
          {adminChallenges.map((item) => <option key={item}>{item}</option>)}
        </select>
      </label>

      <label>
        Funding, reporting, or audit status
        <select name="funding_status" required defaultValue="">
          <option value="" disabled>Select one</option>
          {fundingStatuses.map((item) => <option key={item}>{item}</option>)}
        </select>
      </label>

      <label>
        Next reporting deadline optional
        <input type="date" name="reporting_deadline" />
      </label>

      <label className="check-row">
        <input type="checkbox" name="consent" value="yes" required />
        <span>I agree to receive the one-pager and practical follow-up from Molded Fortitude Consulting.</span>
      </label>

      <input type="hidden" name="source" value="grant-ready-impact-systems" />
      <input type="hidden" name="lead_tags" value="MFC_GrantReadySystems,MFC_NonprofitOps,MFC_EthicalTech,MFC_LeadMagnet_90DayOnePager" />
      {Object.entries(utms).map(([key, value]) => <input key={key} type="hidden" name={key} value={String(value)} />)}

      <button type="submit" disabled={state === 'submitting'}>
        {state === 'submitting' ? 'Sending...' : 'Send Me the One-Pager'}
      </button>

      {message && <p className={`form-message ${state}`}>{message}</p>}
      {state === 'success' && <a className="inline-link" href={offer.onePagerLink}>Open the one-pager</a>}
    </form>
  );
}
